import {
  CalendarDays,
  CreditCard,
  Gift,
  Tv,
  Trophy,
  Shield,
  LifeBuoy,
  User,
  Bell,
  LogOut,
  UserPlus,
  LogIn,
  Settings,
} from "lucide-react";
import PageRoutes, { UserRoles } from "@/utils/constants";
import { isAdminRole } from "@/v2/lib/nav-items";
import type { LucideIcon } from "lucide-react";
import type { NavItem } from "@/v2/lib/nav-items";

/**
 * Input for the config-driven nav builder. The header component gathers
 * these values from useConfig, env, URL params, and useViewerRole, then
 * calls buildNavItems once to get the full item list. filterNavItems is
 * applied afterwards to narrow by auth/admin state.
 */
export type NavConfigInput = {
  planpage: boolean;
  personalChannel: string;
  tenantName: string;
  insiderEnabled: boolean;
  isTeam: boolean;
  adminRoleId?: number | null;
  retentionSaveFlow?: boolean;
  featureEnabled: boolean;
  isChannelCreated: boolean;
};

export type AvatarMenuItem = {
  key: string;
  label: string;
  icon: LucideIcon;
  route?: string;
  isLogout?: boolean;
};

export type MobileAuthItem = {
  key: string;
  label: string;
  icon: LucideIcon;
  route: string;
  isLogout?: boolean;
};

/**
 * Builds the main navigation items based on tenant config flags.
 * Mirrors the v1 header's conditional nav items (Events, Plans,
 * MyChannel, Gift, Highlights, Team, Admin, Support).
 */
export function buildNavItems(config: NavConfigInput): NavItem[] {
  const items: NavItem[] = [
    {
      key: "events",
      label: "Schedule",
      icon: CalendarDays,
      route: PageRoutes.UpcomingEvent,
    },
  ];

  if (config.planpage) {
    items.push({
      key: "plans",
      label: "Plans",
      icon: CreditCard,
      route: PageRoutes.Packages,
    });
  }

  if (config.insiderEnabled) {
    let channelRoute: string;
    if (!config.featureEnabled) {
      // Mirrors v1: without the feature flag, always land on the personal
      // channel shell and let it open the create-channel tab directly.
      channelRoute = `${PageRoutes.PersonalChannel.replace(":id", "0")}?tab_id=1`;
    } else if (config.isChannelCreated) {
      channelRoute = PageRoutes.PersonalChannel.replace(":id", "0");
    } else {
      channelRoute = PageRoutes.MyNAHLTV.replace(":id", "0").replace(
        ":channel_id",
        "0",
      );
    }
    items.push({
      key: "personal-channel",
      label: `My${config.personalChannel}`,
      icon: Tv,
      route: channelRoute,
      requiresAuth: true,
    });
  }

  if (config.planpage) {
    items.push({
      key: "gift",
      label: "Buy A Gift",
      icon: Gift,
      route: PageRoutes.GiftPage,
      requiresAuth: true,
    });
  }

  const hasHighlights = ["nahl", "dev", "ssm", "usah"].includes(
    config.tenantName,
  );

  if (hasHighlights) {
    items.push({
      key: "highlights",
      label: "Highlights",
      icon: Trophy,
      route: PageRoutes.ShortContent,
      requiresAuth: true,
    });

    if (config.isTeam) {
      items.push({
        key: "team",
        label: "Team",
        icon: Trophy,
        route: PageRoutes.Team,
        requiresAuth: true,
      });
    }
  }

  items.push({
    key: "support",
    label: "Support",
    icon: LifeBuoy,
    route: PageRoutes.ContactUS,
  });

  return items;
}

/**
 * Builds the avatar dropdown menu items shown to authenticated
 * desktop viewers. Mirrors v1: My Account (if planpage), Profile, Logout.
 */
export function buildAvatarMenuItems(config: NavConfigInput): AvatarMenuItem[] {
  const items: AvatarMenuItem[] = [];

  if (config.planpage) {
    items.push({
      key: "account",
      label: "My Account",
      icon: CreditCard,
      route: PageRoutes.AccountPage,
    });
  }

  // "Manage Plan" opens the cancel/retention flow, which only makes sense for a
  // tenant that actually sells plans. A free-only tenant (planpage off, e.g.
  // dexter) has nothing to manage even when retentionSaveFlow is on, so require
  // both.
  if (config.planpage && config.retentionSaveFlow) {
    items.push({
      key: "manage-plan",
      label: "Manage Plan",
      icon: Settings,
      route: PageRoutes.CancelSaveFlow,
    });
  }

  items.push({
    key: "profile",
    label: "Profile",
    icon: User,
    route: PageRoutes.Setting,
  });

  if (isAdminRole(config.adminRoleId)) {
    items.push({
      key: "admin",
      label: "Admin",
      icon: Shield,
      route:
        config.adminRoleId === UserRoles.CUSTOMER_ADMIN
          ? PageRoutes.SliderSetting
          : PageRoutes.Admin,
    });
  }

  items.push({
    key: "logout",
    label: "Logout",
    icon: LogOut,
    isLogout: true,
  });

  return items;
}

/**
 * Builds the mobile-only auth action items shown in the mobile sheet.
 * When logged out: Register, Login. When logged in: just Alerts (Profile,
 * Account, and Logout already live in the desktop avatar menu, which is
 * also shown on mobile, so the sheet only adds the alerts shortcut). The
 * header component selects which group to render based on auth state.
 */
export function buildMobileAuthItems(config: NavConfigInput): {
  loggedIn: MobileAuthItem[];
  loggedOut: MobileAuthItem[];
} {
  const loggedOut: MobileAuthItem[] = [
    {
      key: "register",
      label: "Create Account",
      icon: UserPlus,
      route: PageRoutes.Register,
    },
    {
      key: "login",
      label: "Sign In",
      icon: LogIn,
      route: PageRoutes.Login,
    },
  ];

  const loggedIn: MobileAuthItem[] = [
    {
      key: "alerts",
      label: "Alerts",
      icon: Bell,
      route: PageRoutes.Alert,
    },
  ];

  return { loggedIn, loggedOut };
}
